import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader } from "@/components/Kit";
import { Mail as MailIcon, Send, Trash2, Search } from "lucide-react";

export default function Mail() {
  const [params] = useSearchParams();
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const [compose, setCompose] = useState(false);
  const [toQ, setToQ] = useState("");
  const [toUser, setToUser] = useState(null);
  const [results, setResults] = useState([]);
  const [form, setForm] = useState({ subject: "", body: "" });

  const load = () => api.get("/mail").then((r) => setD(r.data));
  useEffect(() => {
    load();
    const to = params.get("to");
    if (to) { setCompose(true); setToUser(to); api.get(`/profile/${to}`).then((r) => setToQ(r.data.name)).catch(() => {}); }
  }, []);

  const readMail = async (m) => {
    setOpen(m);
    if (!m.read) { try { await api.post(`/mail/${m.mail_id}/read`, {}); load(); } catch { /* */ } }
  };
  const del = async (id) => {
    try { await api.delete(`/mail/${id}`); toast.success("Deleted."); setOpen(null); load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const search = async (val) => {
    setToQ(val); setToUser(null);
    if (val.trim().length < 2) { setResults([]); return; }
    try { const { data } = await api.get(`/users/search?q=${encodeURIComponent(val)}`); setResults(data.users); } catch { /* */ }
  };
  const send = async () => {
    if (!toUser) { toast.error("Pick a recipient."); return; }
    if (!form.subject.trim()) { toast.error("Add a subject."); return; }
    try {
      await api.post("/mail/send", { to_user: toUser, subject: form.subject, body: form.body });
      toast.success("Message sent."); setCompose(false); setForm({ subject: "", body: "" }); setToQ(""); setToUser(null); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!d) return <PageLoader label="Opening comms terminal" />;

  return (
    <div data-testid="mail-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <span className="label-cap text-primary flex items-center gap-2"><MailIcon className="w-4 h-4" /> Comms Terminal</span>
          <h1 className="text-4xl font-black tracking-tighter mt-2 font-head">Mail {d.unread > 0 && <span className="text-accent text-lg align-middle">({d.unread} new)</span>}</h1>
        </div>
        <button onClick={() => { setCompose(true); setOpen(null); }} data-testid="compose-btn" className="btn-primary"><Send className="w-4 h-4" /> Compose</button>
      </div>

      {compose ? (
        <div className="panel-glow p-5 hud-corner space-y-3 max-w-lg" data-testid="compose-panel">
          <div className="relative">
            <label className="label-cap block mb-1">To</label>
            <input data-testid="mail-to" value={toQ} onChange={(e) => search(e.target.value)} placeholder="Search pilot…"
              className="w-full px-3 py-2 rounded-[var(--radius)] bg-muted border border-border text-sm focus:outline-none focus:border-primary" />
            {results.length > 0 && !toUser && (
              <div className="absolute z-10 left-0 right-0 mt-1 panel-glow bg-popover p-1">
                {results.map((r) => (
                  <button key={r.user_id} data-testid={`mailrcpt-${r.user_id}`} onClick={() => { setToUser(r.user_id); setToQ(r.name); setResults([]); }}
                    className="w-full text-left px-3 py-2 text-sm rounded-[var(--radius)] hover:bg-primary hover:text-primary-foreground">{r.name}</button>
                ))}
              </div>
            )}
          </div>
          <div><label className="label-cap block mb-1">Subject</label>
            <input data-testid="mail-subject" value={form.subject} maxLength={120} onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
              className="w-full px-3 py-2 rounded-[var(--radius)] bg-muted border border-border text-sm focus:outline-none focus:border-primary" /></div>
          <div><label className="label-cap block mb-1">Message</label>
            <textarea data-testid="mail-body" value={form.body} maxLength={2000} rows={5} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              className="w-full px-3 py-2 rounded-[var(--radius)] bg-muted border border-border text-sm focus:outline-none focus:border-primary" /></div>
          <div className="flex gap-2"><button onClick={send} data-testid="mail-send-btn" className="btn-primary"><Send className="w-4 h-4" /> Send</button><button onClick={() => setCompose(false)} className="btn-ghost">Cancel</button></div>
        </div>
      ) : open ? (
        <div className="panel-glow p-5 hud-corner max-w-lg" data-testid="mail-view">
          <button onClick={() => setOpen(null)} className="btn-ghost text-xs mb-3">← Inbox</button>
          <div className="label-cap">From {open.from_name}</div>
          <h2 className="font-head font-bold text-xl mt-1">{open.subject}</h2>
          <p className="text-sm text-muted-foreground mt-3 whitespace-pre-wrap">{open.body}</p>
          <button onClick={() => del(open.mail_id)} data-testid="mail-delete-btn" className="btn-ghost text-xs text-accent mt-4"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
        </div>
      ) : (
        <div className="space-y-2">
          {d.mail.length === 0 && <div className="panel p-8 text-center text-muted-foreground">Inbox empty.</div>}
          {d.mail.map((m) => (
            <button key={m.mail_id} data-testid={`mailcard-${m.mail_id}`} onClick={() => readMail(m)}
              className={`w-full text-left panel p-4 flex items-center gap-3 hover:-translate-y-0.5 transition-transform ${!m.read ? "border-primary/50" : ""}`}>
              {!m.read && <span className="w-2 h-2 rounded-full bg-accent shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2"><span className="text-sm font-bold truncate">{m.subject}</span><span className="label-cap text-[9px] shrink-0">{m.from_name}</span></div>
                <p className="text-xs text-muted-foreground truncate">{m.body}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
